import { createServerFn } from "@tanstack/react-start";
import { env } from "cloudflare:workers";
import { getSanityClient } from "../../sanity/lib/client";
import {
  ARTICLES_COUNT_QUERY,
  ARTICLES_PAGE_QUERY,
  ARTICLE_BY_SLUG_QUERY,
  CATEGORIES_WITH_COUNTS_QUERY,
  RECENT_POSTS_BY_AUTHOR_QUERY,
  ALL_TAGS_QUERY,
  RELATED_POSTS_QUERY,
} from "../../sanity/lib/queries";
import type {
  SanityArticle,
  SanityArticleSummary,
  SanityCategoryWithCount,
  SanityRecentPost,
} from "./sanity.types";

/**
 * Matches the paginator shape the old Strapi implementation returned so
 * callers / loaders don't need any code changes beyond swapping the import.
 */
type ArticlesPage = {
  articles: SanityArticleSummary[];
  pagination: {
    page: number;
    pageSize: number;
    pageCount: number;
    total: number;
  };
};

export const getArticles = createServerFn({ method: "GET" })
  .inputValidator(
    (data: { page?: number; pageSize?: number; query?: string; category?: string }) => data,
  )
  .handler(async ({ data }): Promise<ArticlesPage> => {
    const { page = 1, pageSize = 10, query, category } = data;
    const validPage = !page || page < 1 ? 1 : page;
    const start = (validPage - 1) * pageSize;
    const end = start + pageSize;

    try {
      const client = getSanityClient(env);
      // GROQ `match` wants a wildcard to behave like `containsi`; empty
      // string short-circuits the filter in the query body.
      const matchQuery = query && query.trim().length > 0 ? `*${query.trim()}*` : "";
      const categoryFilter = category && category.trim().length > 0 ? category.trim() : "";

      const [articles, total] = await Promise.all([
        client.fetch<SanityArticleSummary[]>(ARTICLES_PAGE_QUERY, {
          start,
          end,
          search: matchQuery,
          category: categoryFilter,
        }),
        client.fetch<number>(ARTICLES_COUNT_QUERY, {
          search: matchQuery,
          category: categoryFilter,
        }),
      ]);

      const safeTotal = typeof total === "number" ? total : 0;
      const pageCount = Math.max(1, Math.ceil(safeTotal / pageSize));

      return {
        articles: articles ?? [],
        pagination: {
          page: validPage,
          pageSize,
          pageCount,
          total: safeTotal,
        },
      };
    } catch (error) {
      console.error("Error fetching articles from Sanity:", error);
      return {
        articles: [],
        pagination: { page: validPage, pageSize, pageCount: 1, total: 0 },
      };
    }
  });

export const getArticle = createServerFn({ method: "GET" })
  .inputValidator((slug: string) => slug)
  .handler(async ({ data: slug }): Promise<SanityArticle | null> => {
    try {
      const client = getSanityClient(env);
      const article = await client.fetch<SanityArticle | null>(ARTICLE_BY_SLUG_QUERY, {
        slug,
      });
      return article ?? null;
    } catch (error) {
      console.error("Error fetching article from Sanity:", error);
      return null;
    }
  });

export const getCategories = createServerFn({ method: "GET" }).handler(
  async (): Promise<SanityCategoryWithCount[]> => {
    try {
      const client = getSanityClient(env);
      const categories = await client.fetch<
        Array<{
          id: string;
          documentId: string;
          name: string;
          slug: string;
          description: string | null;
          count: number;
        }>
      >(CATEGORIES_WITH_COUNTS_QUERY);

      return (categories ?? []).map((category) => ({
        id: category.id,
        documentId: category.documentId,
        name: category.name,
        slug: category.slug,
        description: category.description,
        count: category.count ?? 0,
      }));
    } catch (error) {
      console.error("Error fetching categories from Sanity:", error);
      return [];
    }
  },
);


export const getRecentPostsByAuthor = createServerFn({ method: "GET" })
  .inputValidator((data: { authorSlug: string; currentId: string }) => data)
  .handler(async ({ data }): Promise<SanityRecentPost[]> => {
    try {
      const client = getSanityClient(env);
      const posts = await client.fetch<SanityRecentPost[]>(RECENT_POSTS_BY_AUTHOR_QUERY, {
        authorSlug: data.authorSlug,
        currentId: data.currentId,
      });
      return posts ?? [];
    } catch (error) {
      console.error("Error fetching recent posts by author:", error);
      return [];
    }
  });

export const getAllTags = createServerFn({ method: "GET" }).handler(
  async (): Promise<string[]> => {
    try {
      const client = getSanityClient(env);
      const tags = await client.fetch<string[]>(ALL_TAGS_QUERY);
      return (tags ?? []).slice(0, 8);
    } catch (error) {
      console.error("Error fetching tags:", error);
      return [];
    }
  },
);


export const getRelatedPosts = createServerFn({ method: "GET" })
  .inputValidator((data: { currentId: string; categoryId: string }) => data)
  .handler(async ({ data }): Promise<SanityArticleSummary[]> => {
    try {
      const client = getSanityClient(env);
      const posts = await client.fetch<SanityArticleSummary[]>(RELATED_POSTS_QUERY, {
        currentId: data.currentId,
        categoryId: data.categoryId,
      });
      return posts ?? [];
    } catch (error) {
      console.error("Error fetching related posts:", error);
      return [];
    }
  });
